import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TrophyIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

interface PrizeBreakdownEntry {
  id: string;
  position_label: string;
  category: string;
  prize_amount: number;
  currency: string;
  description?: string;
  display_order: number;
}

interface Tournament {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
}

interface Award {
  id: string;
  name: string;
  description?: string;
}

export default function PrizeBreakdown() {
  const [searchParams] = useSearchParams();
  const tournamentId = searchParams.get('tournament');

  const [prizes, setPrizes] = useState<PrizeBreakdownEntry[]>([]);
  const [awards, setAwards] = useState<Award[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingAwards, setLoadingAwards] = useState(true);
  const [totalPrizePool, setTotalPrizePool] = useState(0);
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('');

  useEffect(() => {
    fetchTournaments();
    fetchAwards();
  }, []);

  useEffect(() => {
    if (tournamentId) {
      setSelectedTournamentId(tournamentId);
    } else if (tournaments.length > 0 && !selectedTournamentId) {
      setSelectedTournamentId(tournaments[0].id);
    }
  }, [tournamentId, tournaments]);

  useEffect(() => {
    if (selectedTournamentId) {
      fetchTournamentDetails();
      fetchPrizeBreakdown();
    }
  }, [selectedTournamentId]);

  const fetchTournaments = async () => {
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name, start_date, end_date')
        .in('status', ['ongoing', 'completed', 'upcoming'])
        .order('start_date', { ascending: false });

      if (error) throw error;

      setTournaments(data || []);
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    }
  };

  const fetchTournamentDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('tournaments')
        .select('id, name, start_date, end_date')
        .eq('id', selectedTournamentId)
        .single();

      if (error) throw error;

      setTournament(data);
    } catch (err) {
      console.error('Error fetching tournament details:', err);
    }
  };

  const fetchPrizeBreakdown = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('prize_breakdown')
        .select('*')
        .eq('tournament_id', selectedTournamentId)
        .order('display_order', { ascending: true });

      if (error) throw error;

      if (data) {
        setPrizes(data);

        const total = data.reduce((sum, prize) => sum + Number(prize.prize_amount), 0);
        setTotalPrizePool(total);
      }
    } catch (err) {
      console.error('Error fetching prize breakdown:', err);
      setPrizes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAwards = async () => {
    try {
      setLoadingAwards(true);

      const { data, error } = await supabase
        .from('award_categories')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      setAwards(data || []);
    } catch (err) {
      console.error('Error fetching awards:', err);
      setAwards([]);
    } finally {
      setLoadingAwards(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = '₦') => {
    return `${currency}${amount.toLocaleString()}`;
  };

  const prizesByCategory = prizes.reduce((acc, prize) => {
    if (!acc[prize.category]) {
      acc[prize.category] = [];
    }
    acc[prize.category].push(prize);
    return acc;
  }, {} as Record<string, PrizeBreakdownEntry[]>);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-golf-green to-golf-orange text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white/20 p-3 rounded-lg">
                <TrophyIcon className="w-12 h-12 text-white" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              Prize Breakdown
            </h1>
            <p className="text-xl md:text-2xl opacity-90">
              {tournament?.name || 'Tournament Prize Distribution'}
            </p>

            {/* Total Prize Pool */}
            {totalPrizePool > 0 && (
              <div className="mt-8 max-w-md mx-auto bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <div className="flex items-center justify-center space-x-3 mb-2">
                  <CurrencyDollarIcon className="w-6 h-6 text-white" />
                  <span className="text-white/90 text-lg font-medium">Total Prize Pool</span>
                </div>
                <div className="text-4xl font-bold text-white">
                  {formatCurrency(totalPrizePool, prizes[0]?.currency)}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tournament Selector */}
          {tournaments.length > 1 && (
            <div className="mb-8">
              <label htmlFor="tournament" className="block text-sm font-medium text-gray-700 mb-2">
                Select Tournament
              </label>
              <select
                id="tournament"
                value={selectedTournamentId}
                onChange={(e) => setSelectedTournamentId(e.target.value)}
                className="w-full max-w-md border border-gray-300 rounded-lg p-3 focus:ring-golf-orange focus:border-golf-orange"
              >
                {tournaments.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-24">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-golf-orange"></div>
              </div>
            ) : prizes.length === 0 ? (
              <div className="text-center py-24">
                <TrophyIcon className="w-20 h-20 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-xl">No prize breakdown available yet</p>
                <p className="text-gray-400 text-sm mt-2">
                  Prize details will be announced soon
                </p>
              </div>
            ) : (
              <div className="p-6 space-y-6">
                {Object.entries(prizesByCategory).map(([category, categoryPrizes]) => (
                  <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                      <h2 className="text-xl font-semibold text-gray-900">{category}</h2>
                    </div>

                    <div className="divide-y divide-gray-200">
                      {categoryPrizes.map((prize, index) => (
                        <div
                          key={prize.id}
                          className={`p-6 hover:bg-gray-50 transition-colors ${
                            index === 0 ? 'bg-yellow-50' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className={`flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center font-bold ${
                                index === 0 ? 'bg-yellow-100 text-yellow-800' :
                                index === 1 ? 'bg-gray-100 text-gray-800' :
                                index === 2 ? 'bg-orange-100 text-orange-800' :
                                'bg-blue-50 text-blue-700'
                              }`}>
                                {index === 0 ? (
                                  <TrophyIcon className="w-7 h-7" />
                                ) : (
                                  <span className="text-lg">{index + 1}</span>
                                )}
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {prize.position_label}
                                </h3>
                                {prize.description && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    {prize.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-3xl font-bold text-golf-orange">
                                {formatCurrency(prize.prize_amount, prize.currency)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Prize Notes */}
                <div className="bg-gray-50 rounded-lg p-6 mt-8">
                  <h3 className="text-base font-semibold text-gray-700 mb-3">Prize Notes</h3>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Prizes are subject to applicable taxes and regulations</li>
                    <li>• Winners must be present to claim their prizes</li>
                    <li>• All decisions by the tournament committee are final</li>
                    <li>• Prize amounts are in Nigerian Naira (₦) unless otherwise specified</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Awards Section */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-8">
            <div className="bg-gradient-to-r from-golf-green to-golf-orange text-white px-6 py-4">
              <div className="flex items-center space-x-3">
                <TrophyIcon className="w-6 h-6" />
                <h2 className="text-2xl font-bold">Tournament Awards</h2>
              </div>
              <p className="text-white/90 text-sm mt-1">
                Special recognition awards for outstanding performance
              </p>
            </div>

            {loadingAwards ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-golf-orange"></div>
              </div>
            ) : awards.length === 0 ? (
              <div className="text-center py-16">
                <TrophyIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No awards available</p>
              </div>
            ) : (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {awards.map((award, index) => (
                    <div
                      key={award.id}
                      className="border-2 border-gray-200 rounded-lg p-5 hover:border-golf-orange hover:shadow-md transition-all"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
                            <TrophyIcon className="w-6 h-6 text-white" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {award.name}
                          </h3>
                          {award.description && (
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {award.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Note:</span> Award winners will be announced at the prize ceremony.
                    Winners are selected based on performance, sportsmanship, and tournament committee decisions.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
